// libraries
import React, { useState, useEffect } from 'react'
import clsx from 'clsx'

// components
import Section from '/react4xp/components/Section'
import Input from '/react4xp/components/Form/Input'
import Button from '/react4xp/components/Button'

// svg
import { GeneralSearch } from '/react4xp/components/Svg/Icons/General'
import { UxArrowDown } from '/react4xp/components/Svg/Ux/Arrow'

// types
interface SearchProps {
    heading?: string
    strings?: {
        searchInput?: {
            smallText?: string
            searchButton?: string
            errorMessage?: string
        }
        searchResults?: {
            title?: string
            noResults?: string
            loadMore?: string
        }
    }
    hideBottomDivider?: boolean
    pagination?: {
        page?: number
        pageSize?: string
        endpoint?: string
    }
    startValues?: {
        search?: string
    }
}

export default function Search({
    heading = 'Søk',
    strings = {
        searchInput: {
            smallText: 'Hva leter du etter?',
            searchButton: 'Søk',
            errorMessage: 'Dette feltet er obligatorisk'
        },
        searchResults: {
            title: 'Søkeresultat',
            noResults: 'Ingen idéer funnet',
            loadMore: 'Last inn knapp'
        }
    },
    hideBottomDivider = false,
    pagination = {},
    startValues = {}
}: SearchProps) {
    const [results, setResults] = useState<any[]>([])
    const [page, setPage] = useState(pagination.page || 1)
    const [hasNextPage, setHasNextPage] = useState(false)
    const [total, setTotal] = useState(0)

    const [inputValue, setInputValue] = useState(startValues.search || '')
    const [searchTerm, setSearchTerm] = useState(startValues.search || '')
    const [isResultsVisible, setIsResultsVisible] = useState(false)

    const [isLoading, setIsLoading] = useState(false)
	const [isLoadingMore, setIsLoadingMore] = useState(false)

    useEffect(() => {
        if (searchTerm) {
            handleFetchResults()
        }
    }, [searchTerm, page])

    const handleFetchResults = async () => {
        if (!pagination.endpoint) return

		const isLoadMoreRequest = page > 1

		if (isLoadMoreRequest) {
			setIsLoadingMore(true)
		} else {
			setIsLoading(true)
		}

        const url = new URL(pagination.endpoint)
        const queryParams = new URLSearchParams()

        if (page) queryParams.append('page', String(page))
        if (pagination.pageSize) queryParams.append('pageSize', pagination.pageSize)
        if (searchTerm) queryParams.append('search', searchTerm)

        url.search = queryParams.toString()

		if (!isLoadMoreRequest) {
			updateCurrentUrl()
		}

        try {
            const response = await fetch(url)
            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.statusText}`)
            }
            const data = await response.json()

			setResults((prevResults) => {
				if (isLoadMoreRequest) {
					return [...prevResults, ...data.hits]
				}

				return data.hits
			})

            setHasNextPage(data.hasNextPage)
            setTotal(data.total)

        } catch (error) {
            console.error('There was a problem with the fetch operation:', error)
        }

        setIsLoading(false)
		setIsLoadingMore(false)
        setIsResultsVisible(true)
    }

    const updateCurrentUrl = () => {
        const origin = window.location.origin;
        const updatedUrl = new URL(`${origin}${window.location.pathname}`);
        const queryParams = new URLSearchParams();

        if (searchTerm) queryParams.append('search', searchTerm);

        updatedUrl.search = queryParams.toString();
        window.history.pushState({}, '', updatedUrl);
    }

    const handleLoadMore = () => {
        if (hasNextPage && !isLoadingMore) {
			setIsLoadingMore(true)
            setPage((prevPage) => prevPage + 1)
        }
    }

    const handleSearchTerm = () => {
        setPage(1)
        setSearchTerm(inputValue)

        setTimeout(() => {
			const searchResultsElement = document.getElementById('search-results')
			if (searchResultsElement) {
				const headerOffset = 0
				const elementPosition = searchResultsElement.getBoundingClientRect().top + window.scrollY
				const offsetPosition = elementPosition - headerOffset
	
				window.scrollTo({
					top: offsetPosition,
					behavior: 'smooth'
				})
			}
		}, 100)
	}

    // highlight search term
	const highlightText = (text, highlight) => {
		if (!highlight.trim()) {
			return text
		}
		const regex = new RegExp(`(${highlight})`, 'gi')
		return text.split(regex).map((part, i) => 
			regex.test(part) ? <span key={i} className='highlight'>{part}</span> : part
		)
	}

	return (
		<Section className='page-search' marginTop='small'>
			<div className='container'>

				<div className='row'>
					<div className='col-lg-10 offset-lg-1 col-xl-6 offset-xl-3'>

						<div className='page-search__search-box'>

                            <h1 className='h1-hero-small'>
                                {heading}
                            </h1>

                            <div className='search-form'>

                                <Input
                                    id='search'
                                    size='large'
                                    name={strings.searchInput?.smallText}
                                    label={strings.searchInput?.smallText}
                                    type='text'
                                    required
                                    errorMessages={{
                                        required: strings.searchInput?.errorMessage
                                    }}
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyDown={(e) => {
										if (e.key === 'Enter') {
											e.preventDefault()
											handleSearchTerm()
										}
									}}
                                />

                                <Button
                                    isSolid
                                    noUnderline
                                    smallBorders
                                    isLoading={isLoading}
									disabled={isLoading || isLoadingMore}
                                    onClick={handleSearchTerm}
                                >
                                    {strings.searchInput?.searchButton} <GeneralSearch variant='outline' />
                                </Button>

                            </div>

						</div>

                    </div>
                </div>

                {isResultsVisible && (
                    <div
                        className={clsx(
                            'page-search__search-results',
                            hideBottomDivider && 'page-search__search-results--no-bottom-divider'
                        )}
                        id='search-results'
                    >
                        <div className='row'>
                            <div className='col-lg-10 offset-lg-1 col-xl-6 offset-xl-3'>

                                <h2 className='h1-hero-small'>
                                    {strings.searchResults?.title}
                                </h2>

                                {results.length > 0 ? (
                                    <div className='list'>
                        
                                        {results.map((item, i) => (
                                            <a
                                                href={item.url}
                                                className='item'
                                                key={i}
                                            >

                                                <p className='search-result-title text-big bold'>
                                                    {highlightText(item.title, searchTerm)}
                                                </p>

                                                <p className='search-result-description'>
                                                    {highlightText(item.description, searchTerm)}
                                                </p>

                                            </a>
                                        ))}
                                    </div>
                                ) : (
                                    <div className='no-results'>
                                        <h2 className='h2'>
                                            {strings.searchResults?.noResults}
                                        </h2>
                                    </div>
                                )}

                                {hasNextPage && (
                                    <Button
                                        className='load-more'
                                        noUnderline
										isLoading={isLoadingMore}
										disabled={isLoadingMore}
                                        onClick={handleLoadMore}
                                        bigButton
                                    >
                                        {strings.searchResults?.loadMore} <UxArrowDown />
                                    </Button>
                                )}

                            </div>
                        </div>
                    </div>
                )}

			</div>
		</Section>
	)
}
